import {
  Box,
  Button,
  CascaderField,
  Flex,
  Form,
  Space,
  SubmitButton,
  TextInputField,
} from '@/components';
import { useOrganizationOptionsRecursive } from '@/features/tenant-manager/hooks';
import { SearchUsersModalFormSchema } from '@/features/tenant-manager/models';
import { useForm } from '@/hooks';
import { currentFiscalYear } from '@/utils';
import { zodResolver } from '@hookform/resolvers/zod';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';

type FormValues = z.infer<typeof SearchUsersModalFormSchema>;

type Props = {
  referenceFiscalYear?: string;
  onSubmit: (values: FormValues) => void;
  onClear: () => void;
};

export const SearchUsersForm: FC<Props> = ({
  referenceFiscalYear = currentFiscalYear(),
  onSubmit,
  onClear,
}) => {
  const { t } = useTranslation();

  const {
    control,
    reset,
    formState: { errors },
    handleSubmit,
  } = useForm<Partial<FormValues>, FormValues>({
    resolver: zodResolver(SearchUsersModalFormSchema),
  });

  const organizationOptions = useOrganizationOptionsRecursive({
    params: {
      referenceFiscalYear,
    },
  });

  return (
    <Form
      leavePageAlertCondition={null}
      layout="vertical"
      onFinish={handleSubmit(onSubmit)}
    >
      <Flex vertical gap="middle">
        <Flex gap="middle" align="end">
          <TextInputField
            name="userName"
            autocompleteKey="SearchUsersModal.userName"
            label={t('user-name')}
            control={control}
            errors={errors}
            marginBottom={0}
          />
          <Box flex={1}>
            <CascaderField
              name="organizationIds"
              label={t('organization')}
              control={control}
              errors={errors}
              options={organizationOptions.data}
              loading={organizationOptions.isLoading}
              multiple
              wrapperStyle={{ marginBottom: 0 }}
            />
          </Box>
        </Flex>
        <Space>
          <Button
            onClick={() => {
              onClear();
              reset();
            }}
          >
            {t('search-reset')}
          </Button>
          <SubmitButton text={t('search')} />
        </Space>
      </Flex>
    </Form>
  );
};
